import React, { useState, useRef } from 'react';
import { View, Text, TextInput } from 'react-native';
import i18n from 'i18n-js';

import { scale } from '@components/frame';
import { isPhone, Alert } from '@helpers';

import TouchableElement from '@components/common/touchableElement';

import { font } from '@styles/fonts';

import { SmallLoading } from '@components/loading';
import { modalFieldBg } from '@v2/pages/common/constants/base.constants';

const Input = ({ value, index, setStartValues, startValues, elRef, nextRef, C, disable, noMargin }) => {
  return (
    <TextInput
      style={[
        {
          backgroundColor: C(disable ? 'Background/Light/Secondary on detail' : modalFieldBg),
          color: C(disable ? 'Label/Light/Tertiary' : 'Label/Light/Primary'),
          height: scale(42),
          flex: 1,
          borderRadius: scale(10),
          marginRight: scale(noMargin ? 0 : 18),
          textAlign: 'center',
          fontSize: scale(16),
          fontWeight: '400',
          minWidth: scale(50),
        },
      ]}
      editable={!disable}
      value={value}
      keyboardType={'number-pad'}
      ref={elRef}
      onChangeText={(text) => {
        if (disable) {
          return;
        }
        if (!text) {
          setStartValues({
            ...startValues,
            [index]: '',
          });
          return;
        }

        let number = parseInt(text) || 0;
        if (number > 254) {
          number = 254;
        }

        if (number < 0) {
          number = 0;
        }

        setStartValues({
          ...startValues,
          [index]: String(number),
        });

        if (Number(`${number}0`) > 254) {
          if (nextRef) {
            nextRef?.current?.focus();
          } else {
            elRef?.current?.blur();
          }
        }
      }}
    />
  );
};
export default ({ C, setMode, MODES, setSelectedRange, selectedRange, configureIpRangeForScan, removeIpRangeFromScan }) => {
  const [isLoading, setIsLoading] = useState(false);

  const [range, setRange] = useState(selectedRange || {});

  const startBase = selectedRange?.startIpAddress?.split('.');

  const [startValues, setStartValues] = useState(startBase ? { 1: startBase[0], 2: startBase[1], 3: startBase[2] } : {});

  const input1 = useRef(null);
  const input2 = useRef(null);
  const input3 = useRef(null);

  const submit = async (isExclusion) => {
    const base = `${startValues[1]}.${startValues[2]}.${startValues[3]}`;

    await configureIpRangeForScan({
      workerType: 'machines',
      ipRange: {
        rangeId: range.rangeId || null,
        startIpAddress: `${base}.1`,
        endIpAddress: `${base}.254`,
        isExclusion: isExclusion,
      },
    });
  };

  const isSaveDisabled = !startValues[1] || !startValues[2] || !startValues[3];

  return (
    <View style={{ paddingBottom: scale(15) }}>
      <View>
        <View
          style={{
            width: '100%',
            zIndex: 1000,
            height: scale(58),
          }}
        >
          <View style={{ position: 'absolute', zIndex: 1000, left: 0 }}>
            <TouchableElement
              onPress={() => {
                setSelectedRange(null);
                setMode(MODES.SEARCH_SETTINGS);
              }}
            >
              {({ boxStyle, pressHoverColor }) => (
                <View style={[boxStyle, { padding: scale(15) }]}>
                  <Text style={[{ color: C(pressHoverColor('System/Light/Blue')) }, font.Body]}>{i18n.t('Cancel')}</Text>
                </View>
              )}
            </TouchableElement>
          </View>
          <View style={{ position: 'absolute', zIndex: 1000, right: 0 }}>
            <TouchableElement
              onPress={async () => {
                if (isSaveDisabled) {
                  return;
                }
                const finish = async () => {
                  setIsLoading(true);
                  await submit(range.isExclusion);
                  setIsLoading(false);
                  setSelectedRange(null);
                  setMode(MODES.SEARCH_SETTINGS);
                };

                if (range.isExclusion) {
                  Alert({
                    title: i18n.t('Are you sure?'),
                    text: i18n.t('The Smart Factory Connector will NOT search in an IP exclusion range for machines it can connect to.'),
                    position: 'vertical',
                    actions: [
                      {
                        text: i18n.t('No, keep searching'),
                        onPress: () => {},
                        style: 'primary',
                      },
                      {
                        text: i18n.t('Yes, stop searching'),
                        onPress: async () => {
                          await finish();
                        },
                        style: 'danger',
                      },
                    ],
                  });
                } else {
                  await finish();
                }
              }}
            >
              {({ boxStyle, pressHoverColor }) => (
                <View style={[boxStyle, { padding: scale(15) }]}>
                  {isLoading ? (
                    <SmallLoading />
                  ) : (
                    <Text style={[{ color: C(pressHoverColor(isSaveDisabled ? 'Label/Light/Quinary' : 'Link/Light/Primary')) }, font.Headline]}>
                      {i18n.t('Save')}
                    </Text>
                  )}
                </View>
              )}
            </TouchableElement>
          </View>
        </View>
        <View style={{ width: '100%', position: 'absolute', zIndex: 100, top: scale(15) }}>
          <Text style={[{ color: C('Label/Light/Primary'), width: '100%', textAlign: 'center' }, font.Headline]}>
            {i18n.t(selectedRange?.header || 'Machine IP Range')}
          </Text>
        </View>
      </View>
      <View style={{ marginTop: scale(20) }}>
        <View style={{ paddingHorizontal: scale(isPhone ? 0 : 15), marginTop: scale(7) }}>
          <View style={{ paddingHorizontal: scale(15) }}>
            <View style={{ flexDirection: 'row' }}>
              <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>{i18n.t('START IP ADDRESS')}</Text>
            </View>
            <View style={{ flexDirection: 'row' }}>
              <Input {...{ C, value: startValues[1], index: 1, setStartValues, startValues, elRef: input1, nextRef: input2 }} />
              <Input {...{ C, value: startValues[2], index: 2, setStartValues, startValues, elRef: input2, nextRef: input3 }} />
              <Input {...{ C, value: startValues[3], index: 3, setStartValues, startValues, elRef: input3 }} />
              <Input {...{ C, value: '1', disable: true, noMargin: true }} />
            </View>
            <View style={{ marginTop: scale(25), flexDirection: 'row' }}>
              <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>{i18n.t('END IP ADDRESS')}</Text>
            </View>
            <View style={{ flexDirection: 'row' }}>
              <Input {...{ C, value: startValues[1], disable: true }} />
              <Input {...{ C, value: startValues[2], disable: true }} />
              <Input {...{ C, value: startValues[3], disable: true }} />
              <Input {...{ C, value: '254', disable: true, noMargin: true }} />
            </View>
            <View style={{ marginTop: scale(5) }}></View>
            <View style={{ marginTop: scale(6), flexDirection: 'row' }}>
              <Text style={[{ color: C('Label/Light/Tertiary') }, font['Caption 1']]}>
                {i18n.t(
                  range.isExclusion
                    ? 'The Smart Factory Connector will NOT search in this IP exclusion range for machines it can connect to.'
                    : 'The Smart Factory Connector will search in this IP range for machine it can connect to.'
                )}
              </Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};
